import type { TFunction } from "i18next";
import {
  AlertCircle,
  ArrowUpNarrowWide,
  Box,
  CalendarClock,
  CalendarDaysIcon,
  Hash,
  Layers,
  MessageSquareQuote,
  PackageOpen,
  RefreshCw,
  StickyNote,
  UserIcon,
} from "lucide-react";
import { startCase } from "lodash";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { RequestInfoItem } from "@/components/request-detail";
import type { ISingleAssetRequest } from "@/types/(company)/assets.interface";
import { AssetCategories } from "@/utils/enums";
import { formatDate, formatPerson } from "@/utils/helper";
import { InventoryColumn } from "./InventoryColumn";

/** AssetCategories values. Falls back to a humanized slug for anything else. */
const ASSET_CATEGORY_KEYS: Record<string, string> = {
  regular: "assets:regular",
  consumable: "assets:consumable",
};
const assetCategoryLabel = (t: TFunction, category?: string) =>
  category
    ? ASSET_CATEGORY_KEYS[category]
      ? t(ASSET_CATEGORY_KEYS[category])
      : startCase(category)
    : "";

/** ENUM_PRIORITY_OPTIONS values. Falls back to a humanized slug for anything else. */
const PRIORITY_KEYS: Record<string, string> = {
  low: "common:priority.low",
  medium: "common:priority.medium",
  high: "common:priority.high",
  urgent: "common:priority.urgent",
};
const priorityLabel = (t: TFunction, priority?: string) =>
  priority
    ? PRIORITY_KEYS[priority]
      ? t(PRIORITY_KEYS[priority])
      : startCase(priority)
    : "";

// ── Shared info items builder ─────────────────────────────────────────────────

/**
 * Builds the RequestInfoGrid items array for any asset request detail page.
 * Both AssetRequestView and asset-request/index use this — eliminates ~120
 * duplicated lines between the two pages.
 */
export function buildAssetInfoItems(
  request: ISingleAssetRequest | null | undefined,
  t: TFunction,
): RequestInfoItem[] {
  const assetName =
    request?.assetCategory === AssetCategories.Consumable
      ? (request?.consumable?.name ?? "—")
      : (request?.assetType?.name ?? "—");

  return [
    {
      key: "requestedBy",
      icon: (
        <Avatar className="size-7 bg-primary/10">
          <AvatarImage
            src={request?.user?.profileImage ?? ""}
            alt={t("assets:profile")}
          />
          <AvatarFallback>
            <UserIcon size={14} className="text-white" />
          </AvatarFallback>
        </Avatar>
      ),
      tone: "indigo",
      label: t("assets:requested_by"),
      value: formatPerson(request?.user),
    },
    {
      key: "requestId",
      icon: <Hash className="w-5 h-5 text-primary" />,
      tone: "indigo",
      label: t("assets:request_id"),
      value: (
        <span className="font-mono font-semibold text-primary">
          {request?.requestId ?? `AR${request?.id}`}
        </span>
      ),
    },
    {
      key: "category",
      icon: <Layers className="w-5 h-5 text-[#7B5BD6]" />,
      tone: "purple",
      label: t("assets:asset_category"),
      value: (
        <span className="uppercase">
          {assetCategoryLabel(t, request?.assetCategory) || "—"}
        </span>
      ),
    },
    {
      key: "item",
      icon: <Box className="w-5 h-5 text-[#1AD598]" />,
      tone: "green",
      label: t("assets:item"),
      value: assetName,
    },
    {
      key: "requestType",
      icon: <RefreshCw className="w-5 h-5 text-[#0A94FF]" />,
      tone: "blue",
      label: t("assets:request_type"),
      value: startCase(request?.requestType ?? ""),
      hidden: !request?.requestType,
    },
    {
      key: "quantity",
      icon: <PackageOpen className="w-5 h-5 text-[#7C3AED]" />,
      tone: "purple",
      iconBg: "bg-[#F3E8FF]",
      label: t("assets:request_quantity"),
      value:
        request?.assetCategory === AssetCategories.Regular ? (
          String(request.requestedQuantity ?? "—")
        ) : (
          <InventoryColumn
            inventory={request?.availableInventory ?? null}
            field="issuedUnits"
          />
        ),
      hidden: !(
        request?.requestedQuantity ?? request?.consumableRequiredQuantity
      ),
    },
    {
      key: "priority",
      icon: <ArrowUpNarrowWide className="w-5 h-5 text-[#F3654A]" />,
      tone: "red",
      label: t("assets:priority"),
      value: (
        <span className="uppercase">
          {priorityLabel(t, request?.priority) || "—"}
        </span>
      ),
    },
    {
      key: "requestedOn",
      icon: <CalendarDaysIcon className="w-5 h-5 text-[#7B5BD6]" />,
      tone: "purple",
      label: t("assets:requested_on"),
      value: formatDate(request?.requestDate ?? request?.createdAt ?? null),
      hidden: !(request?.requestDate ?? request?.createdAt),
    },
    {
      key: "requiredBy",
      icon: <CalendarClock className="w-5 h-5 text-[#1AD598]" />,
      tone: "green",
      label: t("assets:required_by"),
      value: formatDate(request?.requiredDate ?? null),
      hidden: !request?.requiredDate,
    },
    {
      key: "reason",
      icon: <MessageSquareQuote className="w-5 h-5 text-[#0A94FF]" />,
      tone: "blue",
      label: t("assets:reason_for_request"),
      value: request?.description ?? request?.reason ?? "—",
      fullWidth: true,
      hidden: !(request?.description || request?.reason),
    },
    {
      key: "notes",
      icon: <StickyNote className="w-5 h-5 text-primary" />,
      tone: "indigo",
      label: t("assets:approval_notes"),
      value: request?.notes ?? "",
      fullWidth: true,
      hidden: !request?.notes,
    },
    {
      key: "rejectionReason",
      icon: <AlertCircle className="w-5 h-5 text-danger-500" />,
      tone: "danger",
      label: t("assets:rejection_reason"),
      value: (
        <span className="text-danger-600">{request?.rejectionReason}</span>
      ),
      fullWidth: true,
      hidden: !request?.rejectionReason,
    },
  ];
}
